/* Improved version of TimelineChart -- pass in information like chart data using
   subcomponents. */

import { PlotRelayoutEvent } from "plotly.js";
import { Children, PropsWithChildren, createContext, useReducer } from "react";
import MDSpinner from "react-md-spinner";

import Plot from "../Plot";
import styles from "../timeseries-chart/timeseries-chart.module.css";

import { TimelineChartDispatch, TimelineChartState, timelineChartReducer } from "./reducer";
import { stateToPlotlyLayout } from "./state-to-plotly-layout";
import { TimelineChartChart } from "./subcomponents/chart";
import { ChartEmpty, isChartEmptyComponent } from "./subcomponents/chart-empty";
import { ChartLoading, isChartLoadingComponent } from "./subcomponents/chart-loading";
import { Legend } from "./subcomponents/legend";
import { VerticalLine } from "./subcomponents/vertical-line";

export interface TimelineChartProps {
  start: Date;
  end: Date;
  large: boolean;
  yAxes: string[] /* 1 or 2 elements; parameter / unit / reference */;
  widthPx: number;
  heightPx: number;
  noDataMessage?: string | JSX.Element;
  onZoom?: (e: PlotRelayoutEvent) => void;
  isCumulative?: boolean;
  hoverdistance?: number; // Optional hover distance for tooltips
}

// Define a context that passes "dispatch" to the subcomponents
interface TimelineContext {
  dispatch: TimelineChartDispatch;
  currentState: TimelineChartState;
}
export const TimelineContext = createContext<TimelineContext>({
  dispatch: () => {
    throw new Error("TimelineContext not initialized");
  },
  currentState: {
    start: new Date(),
    end: new Date(),
    large: false,
    yAxes: [],
    charts: [],
    verticalLines: [],
    width: 800,
    height: 600,
    isCumulative: false,
    showlegend: false,
    legend: undefined,
    datarevision: 1, // Initialize datarevision
  },
});

export function getInitialState(props: TimelineChartProps): TimelineChartState {
  const {
    start,
    end,
    large,
    yAxes,
    widthPx: width,
    heightPx: height,
    isCumulative = false,
    hoverdistance,
  } = props;

  if (yAxes.length === 0 || yAxes.length > 2)
    throw new Error(`Pass either 1 or 2 yAxes to TimelineChart, not ${JSON.stringify(yAxes)}.`);

  return {
    start,
    end,
    large,
    yAxes,
    charts: [],
    verticalLines: [],
    width,
    height,
    isCumulative,
    showlegend: false,
    legend: undefined,
    datarevision: 1,
    hoverdistance,
  };
}

export function TimelineChart({ children, ...props }: PropsWithChildren<TimelineChartProps>) {
  const [currentState, dispatch] = useReducer(timelineChartReducer, getInitialState(props));

  const { layout, data, chartIsLoading, chartIsEmpty } = stateToPlotlyLayout(currentState);

  const allChildren = Children.toArray(children);

  const normalChildren = allChildren.filter(
    (child: unknown) =>
      !isChartLoadingComponent(child) && !isChartEmptyComponent(child) && child !== null
  );
  const emptyChildren = allChildren.filter(isChartEmptyComponent);
  const loadingChildren = allChildren.filter(isChartLoadingComponent);

  return (
    <div className={styles["TimelineChartContainer"]}>
      <TimelineContext.Provider value={{ dispatch, currentState }}>
        {normalChildren}
      </TimelineContext.Provider>
      {chartIsLoading ? (
        loadingChildren.length > 0 ? (
          loadingChildren
        ) : (
          <div className={styles["TimelineChartOverlay"]}>
            <MDSpinner />
          </div>
        )
      ) : null}
      {chartIsEmpty ? (
        emptyChildren.length > 0 ? (
          emptyChildren
        ) : (
          <div className={styles["TimelineChartOverlay"]}>
            No data available for the selected period.
          </div>
        )
      ) : null}
      <Plot
        className="fullPlot"
        layout={layout}
        data={data}
        config={{ displayModeBar: false, doubleClick: false, showTips: false }}
        onRelayout={props.onZoom}
      />
    </div>
  );
}

TimelineChart.Chart = TimelineChartChart;
TimelineChart.VerticalLine = VerticalLine;
TimelineChart.Empty = ChartEmpty;
TimelineChart.Loading = ChartLoading;
TimelineChart.Legend = Legend;

export default TimelineChart;
